import type { ReactNode } from "react"
import HoverCard from "@/v2-ui/hoverCard"
import LicenseProvenance from "@/v2-backoffice-client/license/changes/LicenseProvenance"
import {
  hasProvenance,
  type LicenseProvenanceLike,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"
import LicenseFeatureGroupInsideList from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupInsideList"

export type LicenseFeatureGroupDetailsFact = {
  label: string
  value: ReactNode
}

export type LicenseFeatureGroupDetailsHoverCardProps = {
  title: string
  /** The registry code, shown in monospace under the title. */
  code?: string | null
  description?: string | null
  facts?: readonly LicenseFeatureGroupDetailsFact[]
  /** Where the surface came from (date, release, issue, PR), when known. */
  provenance?: LicenseProvenanceLike | null
  /**
   * A capability's contents: listed inside the card, scrolling in place,
   * loaded when the card opens. Omitted for rows that are not collections.
   */
  inside?: { surfaceCode: string; containedFeatureCount: number }
  /** Anything else the card ends with — a package's members, a list. */
  footer?: ReactNode
  /** The element that opens the card on hover; rendered as-is (asChild). */
  children: ReactNode
}

// The context behind a row — code, description, where it lives, who carries
// it, and for a capability what it contains — on hover, so the page shows
// only names and the detail is one pointer away. Opens below the name, left-
// aligned with it, after a pause long enough that scanning a list does not
// pop a card at every row. One card for capabilities, modules and content,
// so every row in the editor explains itself the same way.
//
// The card never outgrows the viewport: a module root carries a dozen facts
// and, opened, a list of hundreds of contained rows, and a card taller than
// the room below the trigger used to run off the bottom of the screen with
// no way to scroll it. Radix measures the room on each side
// (--radix-hover-card-content-available-height/-width); the card caps itself
// to it and scrolls inside.
function LicenseFeatureGroupDetailsHoverCard(
  props: LicenseFeatureGroupDetailsHoverCardProps,
) {
  const {
    title,
    code,
    description,
    facts = [],
    provenance,
    inside,
    footer,
    children,
  } = props
  return (
    <HoverCard openDelay={450} closeDelay={120}>
      <HoverCard.Trigger asChild>{children}</HoverCard.Trigger>
      <HoverCard.Content
        side="bottom"
        align="start"
        sideOffset={6}
        collisionPadding={12}
        className="flex w-96 max-w-[var(--radix-hover-card-content-available-width)] flex-col gap-2 text-xs max-h-[var(--radix-hover-card-content-available-height)] overflow-y-auto overscroll-contain"
      >
        <div>
          <div className="text-sm font-medium">{title}</div>
          {code && (
            <div className="font-mono text-[11px] text-muted-foreground">
              {code}
            </div>
          )}
        </div>
        {description && <p className="text-muted-foreground">{description}</p>}
        {(facts.length > 0 || (provenance && hasProvenance(provenance))) && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            {facts.map((fact) => (
              <div key={fact.label} className="contents">
                <dt className="text-muted-foreground">{fact.label}</dt>
                <dd className="min-w-0">{fact.value}</dd>
              </div>
            ))}
            {provenance && hasProvenance(provenance) && (
              <div className="contents">
                <dt className="text-muted-foreground">Since</dt>
                <dd className="min-w-0">
                  <LicenseProvenance provenance={provenance} />
                </dd>
              </div>
            )}
          </dl>
        )}
        {inside && inside.containedFeatureCount > 0 && (
          <LicenseFeatureGroupInsideList
            surfaceCode={inside.surfaceCode}
            containedFeatureCount={inside.containedFeatureCount}
          />
        )}
        {footer}
      </HoverCard.Content>
    </HoverCard>
  )
}

export default LicenseFeatureGroupDetailsHoverCard
